import React, { useState, useEffect, useCallback, useRef } from "react";
import "../../assets/css/ZoomModal.scss";
import "../../config";
import { getFullImageUrl, getThumbnailUrl } from '../../utils/imageUrlBuilder';
import { sortImagesByPriority } from '../../utils/imageSorting';

const ZoomModal = ({ images, isOpen, onClose, initialIndex, defaultIndex, isAnalyze = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [fullImageUrl, setFullImageUrl] = useState("");
  const [sortedImages, setSortedImages] = useState([]);
  const [slideDirection, setSlideDirection] = useState(null);
  const thumbnailStripRef = useRef(null);
  const preloadedRef = useRef(new Set());

  // Sort images using shared utility
  useEffect(() => {
    if (isOpen && images && images.length > 0) {
      debugger;
      const sorted = sortImagesByPriority(images, isAnalyze);
      setSortedImages(sorted);

      // Set initial index based on the sorted images
      let targetIndex = 0;
      if (initialIndex !== undefined && initialIndex >= 0) {
        targetIndex = Math.min(initialIndex, sorted.length - 1);
      } else if (defaultIndex !== undefined && defaultIndex >= 0) {
        targetIndex = Math.min(defaultIndex, sorted.length - 1);
      }

      setCurrentImageIndex(targetIndex);
      setSlideDirection(null);
    } else if (isOpen) {
      setSortedImages([]);
      setCurrentImageIndex(0);
    }
  }, [isOpen, images, initialIndex, defaultIndex, isAnalyze]);

  // Reset preload cache when modal closes
  useEffect(() => {
    if (!isOpen) {
      preloadedRef.current.clear();
    }
  }, [isOpen]);

  // Resolve best available URL for an image object (S3 full > PublicPath > RelativePath)
  const resolveImageUrl = useCallback((imageObj) => {
    if (!imageObj) return "";
    return getFullImageUrl(imageObj) || imageObj.PublicPath || imageObj.ImagePath || "";
  }, []);

  // Update full image URL when index changes + preload adjacent
  useEffect(() => {
    if (sortedImages.length > 0 && currentImageIndex !== null && sortedImages[currentImageIndex]) {
      debugger;
      const url = resolveImageUrl(sortedImages[currentImageIndex]);
      if (url) setFullImageUrl(url);

      // Preload adjacent full-res images (only once per URL)
      const preloadIndex = (idx) => {
        if (idx >= 0 && idx < sortedImages.length) {
          const preloadUrl = resolveImageUrl(sortedImages[idx]);
          if (preloadUrl && !preloadedRef.current.has(preloadUrl)) {
            preloadedRef.current.add(preloadUrl);
            const img = new Image();
            img.src = preloadUrl;
          }
        }
      };
      preloadIndex(currentImageIndex + 1);
      preloadIndex(currentImageIndex - 1);
    }
  }, [currentImageIndex, sortedImages, resolveImageUrl]);

  // Auto-scroll active thumbnail into view
  useEffect(() => {
    if (thumbnailStripRef.current && currentImageIndex !== null) {
      const activeThumb = thumbnailStripRef.current.children[currentImageIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentImageIndex]);

  const handleNextImage = useCallback(() => {
    setSlideDirection('right');
    setCurrentImageIndex(prevIndex => (prevIndex + 1) % sortedImages.length);
  }, [sortedImages.length]);

  const handlePrevImage = useCallback(() => {
    setSlideDirection('left');
    setCurrentImageIndex(
      prevIndex => (prevIndex - 1 + sortedImages.length) % sortedImages.length
    );
  }, [sortedImages.length]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowRight") {
      handleNextImage();
    } else if (e.key === "ArrowLeft") {
      handlePrevImage();
    }
  }, [onClose, handleNextImage, handlePrevImage]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || sortedImages.length === 0) return null;

  const handleNavigate = (index) => {
    if (index === currentImageIndex) return;
    setSlideDirection(index > currentImageIndex ? 'right' : 'left');
    setCurrentImageIndex(index);
  };

  const handleAnimationEnd = () => {
    setSlideDirection(null);
  };

  const showNav = sortedImages.length > 1;

  return (
    <div className="zoom-modal">
      <div className="zoom-overlay" onClick={onClose} />
      <div className="zoom-content">
        <button className="zoom-close" onClick={onClose}>
          &times;
        </button>
        {showNav && (
          <button className="zoom-prev" onClick={handlePrevImage}>
            <i className="fa fa-angle-left" aria-hidden="true" />
          </button>
        )}
        <div className="zoom-image-wrapper">
          <img
            src={fullImageUrl}
            alt={`Zoomed ${currentImageIndex + 1}`}
            className={`zoom-image${slideDirection ? ` slide-${slideDirection}` : ''}`}
            onAnimationEnd={handleAnimationEnd}
          />
        </div>
        {showNav && (
          <button className="zoom-next" onClick={handleNextImage}>
            <i className="fa fa-angle-right" aria-hidden="true" />
          </button>
        )}

        {/* Thumbnail strip */}
        {showNav && (
          <div className="zoom-thumbnail-strip" ref={thumbnailStripRef}>
            {sortedImages.map((img, index) => (
              <img
                key={index}
                src={getThumbnailUrl(img) || resolveImageUrl(img)}
                alt={`Thumb ${index + 1}`}
                className={`zoom-thumb${index === currentImageIndex ? ' active' : ''}`}
                onClick={() => handleNavigate(index)}
                draggable={false}
              />
            ))}
          </div>
        )}

        {/* Dot indicators */}
        {showNav && (
          <div className="zoom-thumbnails">
            {sortedImages.map((_, index) =>
              <span
                key={index}
                className={`zoom-dot${index === currentImageIndex ? ' active' : ''}`}
                onClick={() => handleNavigate(index)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoomModal;
